import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { EpisodeFileFormat } from "./datasetEpisode";
import type { DatasetFileKind } from "./datasetFileSign";
import type { PageQuery, PageResp } from "./page";

// 폴더 하나의 파일 목록이다. backend `GET /v1/datasets/{uuid}/files`
// (`extra-artifact-files.md` §4.5). 파일은 세 카탈로그(episode·meta·extra)에 나뉘어 있다.
// 한 폴더에 여러 카탈로그의 파일이 섞일 수 있다. 예: `meta/info.json` 은 meta 이고, 변환기가
// 남긴 `meta/episodes.csv` 는 extra 다. 서버가 목록을 합치고 `kind` 를 포함해 준다.
// 그래서 화면은 카탈로그를 몰라도 폴더를 연다.
//
// 응답에 URL 을 인라인하지 않는다. 행의 `(kind, uuid)` 를 `signDatasetFiles` 에 전달한다.

// 파일 1건이다. backend DatasetFileResp 와 1:1 이다.
export type DatasetFile = {
  // 파일이 속한 카탈로그다. 멤버는 `extra` 파일만 편집(이름 변경·삭제·덮어쓰기)할 수 있다.
  kind: DatasetFileKind;
  uuid: string;
  relative_path: string;
  // source root 기준 부모 폴더 경로(루트 직속이면 '').
  parent_path: string;
  // `relative_path` 의 마지막 세그먼트.
  filename: string;
  file_format: EpisodeFileFormat;
  size_bytes: number | null;
  content_hash: string | null;
  created_at: string;
  modified_at: string;
};

// 서버는 parent 폴더의 직속 파일만 반환한다. 하위 폴더의 파일은 반환하지 않는다.
//   - parent='' 는 루트 직속이다.
//   - parent 를 보내지 않으면 서버가 422 를 반환한다.
// kind 는 한 값만 받는다. filename 은 그 폴더에 이 이름들이 있는지 묻는 probe 다.
export type DatasetFileListQuery = PageQuery & {
  parent: string;
  kind?: DatasetFileKind;
  filename?: readonly string[];
};

// `filename` probe 한 번에 담을 수 있는 이름 수다. 서버 상한과 같다.
export const DATASET_FILE_PROBE_MAX = 200;

// 폴더 파일 page 를 조회한다(relative_path 순). 파일이 없으면 빈 page 를 반환한다(404 아님).
// 404 dataset.not_found 는 dataset 이 없거나 visibility 조건을 충족하지 않는다는 뜻이다.
export async function listDatasetFiles(
  datasetUuid: string,
  query: DatasetFileListQuery,
): Promise<PageResp<DatasetFile>> {
  const response = await apiClient.get<PageResp<DatasetFile>>(
    apiRoutes.dataset.files(datasetUuid),
    { params: query },
  );
  return response.data;
}
